import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TIMEZONE } from "../constans";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Унікальні YYYY-MM, що потрапляють у проміжок дат (календар Києва). */
export function ymListIntersectingKyivRange(rangeStartDate, rangeEndDate) {
    if (!rangeStartDate || !rangeEndDate) return [];
    let d = dayjs(rangeStartDate).tz(TIMEZONE).startOf("month");
    const endM = dayjs(rangeEndDate).tz(TIMEZONE).startOf("month");
    const out = [];
    while (d.isBefore(endM) || d.isSame(endM, "month")) {
        out.push(d.format("YYYY-MM"));
        d = d.add(1, "month");
    }
    return [...new Set(out)];
}

export function participantUserKey(p) {
    const u = p?.userId;
    if (!u) return "";
    if (typeof u === "string") return u;
    if (typeof u === "object" && u !== null) {
        if (u._id != null) return String(u._id);
        if (u.$oid != null) return String(u.$oid);
    }
    return String(u);
}

function normName(s) {
    return String(s || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * По списку з /api/schedule/month (displayName із профілю) і аліасах KeyCRM — id користувача для поля shifts.
 */
export function findScheduleUserIdForAliases(participants, aliases) {
    if (!participants?.length || !aliases?.length) return null;
    const alist = [...new Set(aliases.map(normName).filter(Boolean))];
    const rows = participants.map((p) => ({
        key: participantUserKey(p),
        dn: normName(p.displayName),
    }));

    for (const a of alist) {
        const hit = rows.find(({ dn }) => dn && dn === a);
        if (hit?.key) return hit.key;
    }
    for (const a of alist) {
        if (a.length < 4) continue;
        const hit = rows.find(
            ({ dn }) =>
                dn &&
                (dn.includes(a) || (dn.length >= 4 && a.includes(dn)))
        );
        if (hit?.key) return hit.key;
    }
    return null;
}

/** Робочий день у графіку: повний день або 1 / 2 зміна. */
export function isMarkedShift(raw) {
    return raw === "shift" || raw === "shift1" || raw === "shift2" || raw === true;
}

/**
 * @param {Record<string, { shifts: Record<string, Record<string, unknown>>, participants?: Array}>} scheduleByYm
 * @param {string} userIdStr — ключ shifts
 */
export function countScheduleShiftDaysInRange(scheduleByYm, userIdStr, rangeStartDate, rangeEndDate) {
    if (!scheduleByYm || !userIdStr || !rangeStartDate || !rangeEndDate) return 0;

    let d = dayjs(rangeStartDate).tz(TIMEZONE).startOf("day");
    const end = dayjs(rangeEndDate).tz(TIMEZONE).startOf("day");
    if (end.isBefore(d)) return 0;

    let c = 0;
    while (!d.isAfter(end)) {
        const ym = d.format("YYYY-MM");
        const payload = scheduleByYm[ym];
        const states = payload?.shifts?.[userIdStr] || {};
        const dom = String(d.date());
        if (isMarkedShift(states[dom])) c += 1;
        d = d.add(1, "day");
    }
    return c;
}

/**
 * Кінець інтервалу для «вже відпрацьованих» змін: мінімум із кінця звіту і сьогодні (Київ, початок доби).
 */
export function kyivWorkedThroughEndInclusive(rangeEndDate) {
    if (!rangeEndDate) return null;
    const today = dayjs().tz(TIMEZONE).startOf("day");
    const rEnd = dayjs(rangeEndDate).tz(TIMEZONE).startOf("day");
    return today.isBefore(rEnd) ? today.toDate() : rEnd.toDate();
}

/**
 * За період звіту + «до сьогодні включно» по відмітках /schedule («зміна»).
 */
export function buildScheduleShiftStatsByReactKey(
    scheduleByYm,
    metaRows,
    rangeStartDate,
    rangeEndDate
) {
    const out = {};
    const ymKeys = Object.keys(scheduleByYm || {});
    if (!ymKeys.length || !metaRows?.length) {
        for (const m of metaRows || []) out[m.reactKey] = null;
        return out;
    }
    const participants = scheduleByYm[ymKeys[0]]?.participants || [];
    const workedEnd = kyivWorkedThroughEndInclusive(rangeEndDate);

    for (const m of metaRows) {
        const uid = m.userId ? String(m.userId) : findScheduleUserIdForAliases(participants, m.scheduleMatchAliases);
        if (!uid) {
            out[m.reactKey] = null;
            continue;
        }
        const totalInReportRange = countScheduleShiftDaysInRange(
            scheduleByYm,
            uid,
            rangeStartDate,
            rangeEndDate
        );
        const workedThroughToday = workedEnd
            ? countScheduleShiftDaysInRange(scheduleByYm, uid, rangeStartDate, workedEnd)
            : 0;
        out[m.reactKey] = { workedThroughToday, totalInReportRange };
    }
    return out;
}
